// SchoolLetterHeader.tsx  (updated — only the diff from the original is noted with // ★)
import React, { useState, useRef, useEffect } from "react";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import "./SchoolLetterHeader.css";
import apiClient from "../../stateManagers/services/axiosInterceptor";
import LetterEditor from "./LetterEditor";
import TemplateSetupPanel from "./TemplateSetupPanel"; // ★ import

import { PAGE_W, PAGE_H, SNAP, FONT_SIZES } from "./letterConstants";
export { PAGE_W, PAGE_H, SNAP, ZOOM_STEPS, FONT_SIZES } from "./letterConstants";

export type Align = "left" | "center" | "right";

export interface BaseElement {
  id: string;
  x: number;
  y: number;
  width: number;
}

export interface TextElement extends BaseElement {
  type: "text";
  content: string;
  html: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  align: Align;
}

export interface ImageElement extends BaseElement {
  type: "image";
  content: string;
  src: string | null;
  height: number;
}

export type CanvasElement = TextElement | ImageElement;
export type ElementPatch = Partial<TextElement> | Partial<ImageElement>;

export const SCHOOL = {
  name: "St. Francis Xavier Catholic Primary School",
  address: "Roberts Road, Balby, Doncaster, DN4 0JN",
  phone: "01302 344678",
  website: "www.xavier.doncaster.sch.uk",
  email: "admin@francisxavier.co.uk",
  headerImage: "https://skoolpillar.space/header.png",
  footerImage: "https://skoolpillar.space/footer.png",
};

function makeEl(o: Partial<TextElement> = {}): TextElement {
  const base: TextElement = {
    id: `el${Date.now()}`,
    type: "text",
    content: "Type here",
    html: "Type here",
    x: 80,
    y: 230,
    width: 420,
    fontSize: 13,
    fontFamily: "'DM Sans',sans-serif",
    color: "#1e3a8a",
    bold: false,
    italic: false,
    underline: false,
    align: "left",
  };
  const merged = { ...base, ...o };
  if (!merged.html) merged.html = merged.content;
  return merged;
}

function makeImgEl(o: Partial<ImageElement> = {}): ImageElement {
  return {
    id: `el${Date.now()}`,
    type: "image",
    content: "Signature",
    src: null,
    x: 80,
    y: 700,
    width: 180,
    height: 70,
    ...o,
  };
}

function buildPrint(elements: CanvasElement[], headerSrc: string | null, footerSrc: string | null): string {
  const hTag = headerSrc
    ? `<img src="${headerSrc}" style="width:100%;display:block;-webkit-print-color-adjust:exact;print-color-adjust:exact;position:relative;z-index:1;" alt="header"/>`
    : `<div style="width:100%;height:160px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);position:relative;z-index:1;"></div>`;
  const fTag = footerSrc
    ? `<img src="${footerSrc}" style="position:absolute;bottom:0;left:0;width:100%;display:block;-webkit-print-color-adjust:exact;print-color-adjust:exact;z-index:1;" alt="footer"/>`
    : `<div style="position:absolute;bottom:0;left:0;width:100%;height:110px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);z-index:1;"></div>`;
  const elHTML = elements
    .map((el) => {
      if (el.type === "image") {
        if (!el.src) return "";
        return `<div style="position:absolute;left:${el.x}px;top:${el.y}px;width:${el.width}px;height:${(el as ImageElement).height}px;z-index:2;"><img src="${el.src}" style="width:100%;height:100%;object-fit:contain;-webkit-print-color-adjust:exact;print-color-adjust:exact;" alt="signature"/></div>`;
      }
      return `<div style="position:absolute;left:${el.x}px;top:${el.y}px;width:${el.width}px;z-index:2;font-size:${el.fontSize}px;font-family:${el.fontFamily};color:${el.color};text-align:${el.align};line-height:1.6;white-space:pre-wrap;word-break:break-word;padding:4px 6px;">${el.html != null ? el.html : (el.content ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</div>`;
    })
    .join("");
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,700;1,400&family=Playfair+Display:wght@700&family=Lato:wght@400;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet"/><style>* { margin: 0; padding: 0; box-sizing: border-box; } @page { size: A4; margin: 0; } body { width: 794px; min-height: 1123px; background: white; } .page { position: relative; width: 794px; min-height: 1123px; background: white; overflow: hidden; }</style></head><body><div class="page">${hTag}<div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 2;">${elHTML}</div>${fTag}</div></body></html>`;
}

export default function SchoolLetterHeader() {
  const [elements, setEls] = useState<CanvasElement[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [printing, setPrinting] = useState(false);
  const [letterheadTitle, setLetterheadTitle] = useState("New Letterhead");
  const [zoom, setZoom] = useState(0.75);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false); // ★

  const elementsRef = useRef<CanvasElement[]>(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = (m: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(m);
    toastTimerRef.current = setTimeout(() => setToast(""), 2400);
  };

  const addEl = (o: Partial<TextElement> = {}) => {
    const el = makeEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const addImgEl = (o: Partial<ImageElement> = {}) => {
    const el = makeImgEl(o);
    setEls((p) => [...p, el]);
    setSelId(el.id);
  };

  const updateEl = (id: string, patch: ElementPatch) => {
    setEls((p) => p.map((e) => (e.id === id ? ({ ...e, ...patch } as CanvasElement) : e)));
  };

  const deleteEl = (id: string) => {
    setEls((p) => p.filter((e) => e.id !== id));
    if (selId === id) setSelId(null);
  };

  const duplicateEl = (id: string) => {
    const s = elementsRef.current.find((e) => e.id === id);
    if (!s) return;
    if (s.type === "image") {
      const el = makeImgEl({ ...s, id: undefined as any, x: s.x + 14, y: s.y + 14 });
      setEls((p) => [...p, el]);
      setSelId(el.id);
    } else {
      addEl({ ...(s as TextElement), id: undefined as any, x: s.x + 14, y: s.y + 14 });
    }
  };

  async function generatePdfFromHtml(html: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const element = document.createElement('div');
        element.innerHTML = html;
        document.body.appendChild(element);
        html2canvas(element, { scale: 2, logging: false, useCORS: true }).then((canvas) => {
          const pdf = new jsPDF('p', 'mm', 'a4');
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, 210, 297);
          const blob = pdf.output('blob');
          document.body.removeChild(element);
          resolve(blob);
        }).catch((error) => {
          document.body.removeChild(element);
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  const handleSaveAndPrint = async () => {
    if (!letterheadTitle.trim()) {
      showToast("Please enter a title for this letterhead");
      return;
    }
    if (elements.length === 0) {
      showToast("Add at least one element before saving");
      return;
    }
    setPrinting(true);
    try {
      const printHTML = buildPrint(elements, SCHOOL.headerImage, SCHOOL.footerImage);
      const pdfBlob = await generatePdfFromHtml(printHTML);

      const formData = new FormData();
      formData.append("title", letterheadTitle.trim());
      formData.append("elements_json", JSON.stringify(elements));
      formData.append("files", pdfBlob, "letterhead.pdf");

      const response = await apiClient.post("letterheads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.status === "success" || response.data?.status === "partial_success") {
        showToast("Letterhead saved ✓ Opening print...");
      } else {
        showToast("Save failed");
        return;
      }

      const w = window.open("", "_blank");
      if (w) {
        w.document.write(printHTML);
        w.document.close();
        setTimeout(() => { w.focus(); w.print(); }, 800);
      } else {
        showToast("Pop-up blocked — please allow pop-ups to print.");
      }
    } catch (error) {
      showToast("Failed to save. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <LetterEditor
        elements={elements}
        selId={selId}
        zoom={zoom}
        letterheadTitle={letterheadTitle}
        printing={printing}
        onTitleChange={setLetterheadTitle}
        onAddEl={addEl}
        onAddImgEl={addImgEl}
        onUpdateEl={updateEl}
        onDeleteEl={deleteEl}
        onDuplicateEl={duplicateEl}
        onSaveAndPrint={handleSaveAndPrint}
        onSetSelId={setSelId}
        onOpenTemplateSetup={() => setTemplatePanelOpen(true)} // ★ pass down
        SCHOOL={SCHOOL}
        PAGE_W={PAGE_W}
        PAGE_H={PAGE_H}
        SNAP={SNAP}
        FONT_SIZES={FONT_SIZES}
      />

      {/* ★ Template Setup slide-in panel */}
      <TemplateSetupPanel
        open={templatePanelOpen}
        onClose={() => setTemplatePanelOpen(false)}
      />
    </>
  );
}